import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "menu", "input", "label", "search", "option", "empty"]
  static values = {
    open: { type: Boolean, default: false },
    selected: String,
    searchable: { type: Boolean, default: false }
  }

  connect() {
    this._boundClose = this.closeOnClickOutside.bind(this)
    this._boundKeydown = this.handleKeydown.bind(this)
    this._boundCloseAll = this._onCloseAll.bind(this)
    this._boundValidate = this.validateRequired.bind(this)
    document.addEventListener("click", this._boundClose)
    document.addEventListener("keydown", this._boundKeydown)
    document.addEventListener("select:close-all", this._boundCloseAll)

    // Validate on form submit if required
    const form = this.element.closest("form")
    if (form && this.inputTarget.hasAttribute("required")) {
      form.addEventListener("submit", this._boundValidate)
    }

    // Auto-enable search for 10+ options
    if (!this.searchableValue && this.optionTargets.length >= 10) {
      this.searchableValue = true
    }

    // Show/hide search input
    if (this.hasSearchTarget) {
      const wrapper = this.searchTarget.closest("[data-search-wrapper]")
      if (wrapper) wrapper.classList.toggle("hidden", !this.searchableValue)
    }

    // Set initial label from selected value
    if (this.selectedValue) {
      const item = this.optionTargets.find(el => el.dataset.value === this.selectedValue)
      if (item) {
        const labelEl = item.querySelector("[data-label]")
        this.labelTarget.textContent = (labelEl || item).textContent.trim()
        this.labelTarget.classList.remove("text-neutral-400")
        this.labelTarget.classList.add("text-neutral-900")
      }
    }
  }

  disconnect() {
    document.removeEventListener("click", this._boundClose)
    document.removeEventListener("keydown", this._boundKeydown)
    document.removeEventListener("select:close-all", this._boundCloseAll)
    const form = this.element.closest("form")
    if (form) form.removeEventListener("submit", this._boundValidate)
  }

  toggle(event) {
    event.stopPropagation()
    this.openValue = !this.openValue
  }

  openValueChanged() {
    if (this.openValue) {
      // Close all other open selects before opening this one
      document.dispatchEvent(new CustomEvent("select:close-all", { detail: { except: this.element } }))
      this.positionMenu()
      this.menuTarget.classList.remove("hidden", "opacity-0", "scale-95")
      this.menuTarget.classList.add("opacity-100", "scale-100")
      this.buttonTarget.setAttribute("aria-expanded", "true")

      // Focus search if searchable
      if (this.searchableValue && this.hasSearchTarget) {
        this.searchTarget.value = ""
        this.filterOptions("")
        setTimeout(() => this.searchTarget.focus(), 50)
      }

      // Scroll selected item into view
      const selected = this.menuTarget.querySelector("[data-selected]")
      if (selected) selected.scrollIntoView({ block: "nearest" })
    } else {
      this.menuTarget.classList.add("hidden", "opacity-0", "scale-95")
      this.menuTarget.classList.remove("opacity-100", "scale-100")
      this.buttonTarget.setAttribute("aria-expanded", "false")

      // Clear search
      if (this.hasSearchTarget) {
        this.searchTarget.value = ""
        this.filterOptions("")
      }

      // Clear keyboard focus
      this.clearFocus()
    }
  }

  positionMenu() {
    const rect = this.buttonTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const menuHeight = 280

    if (spaceBelow < menuHeight && rect.top > menuHeight) {
      this.menuTarget.classList.add("bottom-full", "mb-1.5")
      this.menuTarget.classList.remove("top-full", "mt-1.5")
    } else {
      this.menuTarget.classList.add("top-full", "mt-1.5")
      this.menuTarget.classList.remove("bottom-full", "mb-1.5")
    }
  }

  search(event) {
    this.filterOptions(event.target.value.toLowerCase())
  }

  filterOptions(query) {
    let visibleCount = 0
    this.optionTargets.forEach(option => {
      const labelEl = option.querySelector("[data-label]")
      const text = (labelEl || option).textContent.toLowerCase()
      const match = !query || text.includes(query)
      option.classList.toggle("hidden", !match)
      if (match) visibleCount++
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }
  }

  select(event) {
    const item = event.currentTarget
    const value = item.dataset.value
    const labelEl = item.querySelector("[data-label]")
    const label = (labelEl || item).textContent.trim()

    this.inputTarget.value = value
    this.selectedValue = value
    this.labelTarget.textContent = label
    this.labelTarget.classList.remove("text-neutral-400")
    this.labelTarget.classList.add("text-neutral-900")
    this.buttonTarget.classList.remove("border-danger-500")

    // Update visual selection
    this.optionTargets.forEach(el => {
      el.removeAttribute("data-selected")
      el.querySelector(".select-check")?.classList.add("invisible")
    })
    item.setAttribute("data-selected", "")
    item.querySelector(".select-check")?.classList.remove("invisible")

    this.openValue = false
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  validateRequired(event) {
    if (!this.inputTarget.hasAttribute("required")) return
    if (this.inputTarget.value) {
      this.buttonTarget.classList.remove("border-danger-500")
      return
    }
    event.preventDefault()
    this.buttonTarget.classList.add("border-danger-500")
    this.buttonTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  _onCloseAll(event) {
    if (event.detail?.except === this.element) return
    if (this.openValue) this.openValue = false
  }

  closeOnClickOutside(event) {
    if (this.openValue && !this.element.contains(event.target)) {
      this.openValue = false
    }
  }

  handleKeydown(event) {
    if (!this.openValue) return

    if (event.key === "Escape") {
      this.openValue = false
      this.buttonTarget.focus()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const focused = this.menuTarget.querySelector("[data-focused]")
      if (focused) {
        focused.click()
      } else {
        const firstVisible = this.optionTargets.find(el => !el.classList.contains("hidden"))
        if (firstVisible) firstVisible.click()
      }
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.navigateOptions(event.key === "ArrowDown" ? 1 : -1)
    }
  }

  navigateOptions(direction) {
    const visible = this.optionTargets.filter(el => !el.classList.contains("hidden"))
    if (visible.length === 0) return

    const focused = this.menuTarget.querySelector("[data-focused]")
    let index = focused ? visible.indexOf(focused) : -1

    this.clearFocus()

    index = (index + direction + visible.length) % visible.length
    visible[index].setAttribute("data-focused", "")
    visible[index].classList.add("bg-neutral-100")
    visible[index].scrollIntoView({ block: "nearest" })
  }

  clearFocus() {
    const focused = this.menuTarget.querySelector("[data-focused]")
    if (focused) {
      focused.removeAttribute("data-focused")
      focused.classList.remove("bg-neutral-100")
    }
  }
}
